import { useEffect } from "react";
import { message } from "antd";
import { useQuery } from "@tanstack/react-query";
import { GetOrderById, GetSystemConfig } from "../../Api";
import type { InvoiceTemplateContext } from "../templates";

type InvoiceConfig = InvoiceTemplateContext["config"];
type InvoiceOrder = InvoiceTemplateContext["order"];

interface InvoiceData {
  order: InvoiceOrder;
  config: InvoiceConfig;
}

interface UseInvoiceDataParams {
  open: boolean;
  orderId: string | null;
}

const DEFAULT_INVOICE_CONFIG: InvoiceConfig = {
  store_name: "POS Store",
  address: "Store Address",
  phone: "Store Phone",
  currency: "LKR",
  receipt_footer: "Thank you for your business!",
};

const invoiceQueryKeys = {
  detail: (orderId: string | null) => ["invoice", "detail", orderId] as const,
};

const normalizeConfig = (
  config: Partial<InvoiceConfig> | null | undefined,
) => ({
  ...DEFAULT_INVOICE_CONFIG,
  ...(config || {}),
});

const fetchInvoiceData = async (orderId: string): Promise<InvoiceData> => {
  const [orderRes, configRes] = await Promise.all([
    GetOrderById(orderId),
    GetSystemConfig(),
  ]);

  if (!orderRes?.success) {
    throw new Error(orderRes?.message || "Failed to load order details.");
  }

  return {
    order: orderRes.data,
    config: configRes?.success
      ? normalizeConfig(configRes.data)
      : DEFAULT_INVOICE_CONFIG,
  };
};

export const useInvoiceData = ({ open, orderId }: UseInvoiceDataParams) => {
  const query = useQuery<InvoiceData, Error>({
    queryKey: invoiceQueryKeys.detail(orderId),
    enabled: open && Boolean(orderId),
    staleTime: 30 * 1000,
    queryFn: () => fetchInvoiceData(orderId || ""),
  });

  useEffect(() => {
    if (query.error) {
      message.error(query.error.message);
    }
  }, [query.error]);

  return {
    ...query,
    order: query.data?.order ?? null,
    config: query.data?.config ?? DEFAULT_INVOICE_CONFIG,
  };
};
